import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Spinner, Alert, Button, Form, Badge, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faEye, 
  faClock, 
  faMapMarkerAlt, 
  faShoppingBag, 
  faTags,
  faChartLine,
  faDownload,
  faRefresh,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import api from '../../config/api';
import './AnalyticsDashboard.css';

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [locationStats, setLocationStats] = useState([]);
  const [popularProducts, setPopularProducts] = useState([]);
  const [popularCollections, setPopularCollections] = useState([]);
  const [popularPages, setPopularPages] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 7 gün önce
    endDate: new Date().toISOString().split('T')[0] // bugün
  });
  const [performanceTest, setPerformanceTest] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);

  // Performans testi yap
  const runPerformanceTest = async () => {
    try {
      setPerformanceLoading(true);
      setPerformanceTest(null);

      console.log('🔥 Performans testi başlatılıyor...');
      
      const response = await api.get('/analytics/debug/performance');
      
      if (response.data.success) {
        setPerformanceTest(response.data);
        console.log('✅ Performans testi tamamlandı:', response.data);
      } else {
        throw new Error('Performans testi başarısız');
      }

    } catch (error) {
      console.error('❌ Performans test hatası:', error);
      setError('Performans testi sırasında bir hata oluştu: ' + error.message);
    } finally {
      setPerformanceLoading(false);
    }
  };

  // Verileri yükle
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };
      
      console.log('📅 Frontend tarih parametreleri:', params);

      // Paralel olarak tüm verileri yükle
      const [statsResponse, locationResponse, productsResponse, collectionsResponse, pagesResponse] = await Promise.all([
        api.get('/analytics/dashboard/stats', { params }),
        api.get('/analytics/dashboard/location-stats', { params }),
        api.get('/analytics/dashboard/popular-products', { params: { ...params, limit: 10 } }),
        api.get('/analytics/dashboard/popular-collections', { params: { ...params, limit: 10 } }),
        api.get('/analytics/dashboard/popular-pages', { params: { ...params, limit: 10 } })
      ]);

      // Her response'u kontrol et ve veriyi set et
      if (statsResponse.data.success) {
        setStats(statsResponse.data.data);
      } else {
        console.warn('Stats API response failed:', statsResponse.data);
        setStats({
          totalVisitors: 0,
          uniqueVisitors: 0,
          totalPageViews: 0,
          avgTimeSpent: 0
        });
      }

      if (locationResponse.data.success) {
        setLocationStats(locationResponse.data.data || []);
      } else {
        console.warn('Location stats API response failed:', locationResponse.data);
        setLocationStats([]);
      }

      if (productsResponse.data.success) {
        setPopularProducts(productsResponse.data.data || []);
      } else {
        console.warn('Popular products API response failed:', productsResponse.data);
        setPopularProducts([]);
      }

      if (collectionsResponse.data.success) {
        setPopularCollections(collectionsResponse.data.data || []);
      } else {
        console.warn('Popular collections API response failed:', collectionsResponse.data);
        setPopularCollections([]);
      }

      if (pagesResponse.data.success) {
        setPopularPages(pagesResponse.data.data || []);
      } else {
        console.warn('Popular pages API response failed:', pagesResponse.data);
        setPopularPages([]);
      }

    } catch (error) {
      console.error('Analytics data load error:', error);
      setError(`Veriler yüklenirken bir hata oluştu: ${error.message}`);
      
      // Hata durumunda boş veriler set et
      setStats({
        totalVisitors: 0,
        uniqueVisitors: 0,
        totalPageViews: 0,
        avgTimeSpent: 0
      });
      setLocationStats([]);
      setPopularProducts([]);
      setPopularCollections([]);
      setPopularPages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [dateRange]);

  // Tarih aralığı değiştiğinde verileri yenile
  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Verileri yenile
  const handleRefresh = () => {
    loadData();
  };

  // Verileri export et
  const handleExport = () => {
    const data = {
      stats,
      locationStats,
      popularProducts,
      popularCollections,
      dateRange,
      exportDate: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${dateRange.startDate}-to-${dateRange.endDate}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Hızlı tarih seçenekleri
  const quickDateOptions = [
    { label: 'Son 24 Saat', days: 1 },
    { label: 'Son 3 Gün', days: 3 },
    { label: 'Son 7 Gün', days: 7 },
    { label: 'Son 30 Gün', days: 30 },
    { label: 'Son 90 Gün', days: 90 }
  ];

  const handleQuickDateSelect = (days) => {
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - days * 24 * 60 * 60 * 1000);
    
    setDateRange({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    });
  };


  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Analitik veriler yükleniyor...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Hata!</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={handleRefresh}>
            <FontAwesomeIcon icon={faRefresh} className="me-2" />
            Tekrar Dene
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>
                <FontAwesomeIcon icon={faChartLine} className="me-2" />
                Analitik Dashboard
              </h2>
              <p className="text-muted">Kullanıcı davranışları ve site istatistikleri</p>
            </div>
             <div className="d-flex gap-2">
               <Button variant="outline-primary" onClick={handleRefresh}>
                 <FontAwesomeIcon icon={faRefresh} className="me-2" />
                 Yenile
               </Button>
               <Button variant="outline-success" onClick={handleExport}>
                 <FontAwesomeIcon icon={faDownload} className="me-2" />
                 Export
               </Button>
             </div>
          </div>
        </Col>
      </Row>

      {/* Tarih Aralığı Filtresi */}
      <Row className="mb-4">
        <Col md={8}>
          <Card>
            <Card.Body>
              <Card.Title>
                <FontAwesomeIcon icon={faClock} className="me-2" />
                Tarih Aralığı
              </Card.Title>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Başlangıç Tarihi</Form.Label>
                    <Form.Control
                      type="date"
                      value={dateRange.startDate}
                      onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Bitiş Tarihi</Form.Label>
                    <Form.Control
                      type="date"
                      value={dateRange.endDate}
                      onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div>
                <Form.Label className="mb-2">Hızlı Seçenekler:</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {quickDateOptions.map((option, index) => (
                    <Button
                      key={index}
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleQuickDateSelect(option.days)}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <Card.Title>
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                Bilgi
              </Card.Title>
              <div className="small text-muted">
                <p><strong>Toplam Ziyaretçi:</strong> {stats?.totalVisitors || 0}</p>
                <p><strong>Benzersiz Ziyaretçi:</strong> {stats?.uniqueVisitors || 0}</p>
                <p><strong>Sayfa Görüntüleme:</strong> {stats?.totalPageViews || 0}</p>
                <p><strong>Ort. Süre:</strong> {stats?.avgTimeSpent || 0} saniye</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Genel İstatistikler */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <FontAwesomeIcon icon={faUsers} className="stats-icon text-primary" />
              <h3 className="mt-2">{stats?.uniqueVisitors || 0}</h3>
              <p className="text-muted">Benzersiz Ziyaretçi</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <FontAwesomeIcon icon={faEye} className="stats-icon text-info" />
              <h3 className="mt-2">{stats?.totalPageViews || 0}</h3>
              <p className="text-muted">Sayfa Görüntüleme</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <FontAwesomeIcon icon={faClock} className="stats-icon text-warning" />
              <h3 className="mt-2">{stats?.avgTimeSpent || 0}s</h3>
              <p className="text-muted">Ort. Sayfa Süresi</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="stats-card">
            <Card.Body className="text-center">
              <FontAwesomeIcon icon={faShoppingBag} className="stats-icon text-success" />
              <h3 className="mt-2">{stats?.totalVisitors || 0}</h3>
              <p className="text-muted">Toplam Ziyaretçi</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Konum İstatistikleri */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <h5>
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                İl Bazında Ziyaretçi Dağılımı
              </h5>
            </Card.Header>
            <Card.Body>
              {locationStats.length === 0 ? (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-2" size="2x" />
                  <p className="text-muted">Bu tarih aralığında konum verisi bulunamadı.</p>
                </div>
              ) : (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>İl</th>
                      <th>Şehir</th>
                      <th>Benzersiz Ziyaretçi</th>
                      <th>Toplam Oturum</th>
                      <th>Dağılım Oranı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locationStats.map((location, index) => {
                      const percentage = stats?.uniqueVisitors > 0 
                        ? Math.round((location.uniqueVisitors / stats.uniqueVisitors) * 100)
                        : 0;
                      
                      return (
                        <tr key={index}>
                          <td>
                            <Badge bg="primary">{location.region}</Badge>
                          </td>
                          <td>{location.city}</td>
                          <td>{location.uniqueVisitors}</td>
                          <td>{location.totalSessions}</td>
                          <td>
                            <Badge bg="info" className="fs-6">
                              {percentage}%
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Popüler Ürünler ve Koleksiyonlar */}
      <Row>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5>
                <FontAwesomeIcon icon={faShoppingBag} className="me-2" />
                En Popüler Ürünler
              </h5>
            </Card.Header>
            <Card.Body>
              {popularProducts.length === 0 ? (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-2" size="2x" />
                  <p className="text-muted">Bu tarih aralığında ürün etkileşimi verisi bulunamadı.</p>
                </div>
              ) : (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Sıra</th>
                      <th>Ürün</th>
                      <th>Görüntüleme</th>
                      <th>Tıklama</th>
                      <th>Sepete Ekleme</th>
                      <th>Popülerlik Skoru</th>
                    </tr>
                  </thead>
                  <tbody>
                    {popularProducts.map((product, index) => (
                      <tr key={index}>
                        <td>
                          <Badge bg="primary">#{index + 1}</Badge>
                        </td>
                        <td>
                          <div>
                            <strong>{product.productName || 'Bilinmeyen Ürün'}</strong>
                            <br />
                            <small className="text-muted">
                              {product.productPrice ? `₺${product.productPrice}` : 'Fiyat bilgisi yok'}
                            </small>
                          </div>
                        </td>
                        <td>
                          <Badge bg="info">{product.totalViews || 0}</Badge>
                        </td>
                        <td>
                          <Badge bg="warning">{product.totalClicks || 0}</Badge>
                        </td>
                        <td>
                          <Badge bg="success">{product.totalAddToCart || 0}</Badge>
                        </td>
                        <td>
                          <Badge bg="danger" className="fs-6">
                            {product.popularityScore || 0}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h5>
                <FontAwesomeIcon icon={faTags} className="me-2" />
                En Popüler Koleksiyonlar
              </h5>
            </Card.Header>
            <Card.Body>
              {popularCollections.length === 0 ? (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-2" size="2x" />
                  <p className="text-muted">Bu tarih aralığında koleksiyon etkileşimi verisi bulunamadı.</p>
                </div>
              ) : (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Sıra</th>
                      <th>Koleksiyon</th>
                      <th>Görüntüleme</th>
                      <th>Tıklama</th>
                      <th>Popülerlik Skoru</th>
                    </tr>
                  </thead>
                  <tbody>
                    {popularCollections.map((collection, index) => {
                      return (
                        <tr key={index}>
                          <td>
                            <Badge bg="primary">#{index + 1}</Badge>
                          </td>
                          <td>
                            <strong>{collection.collectionName || 'Bilinmeyen Koleksiyon'}</strong>
                          </td>
                          <td>
                            <Badge bg="info">{collection.totalViews || 0}</Badge>
                          </td>
                          <td>
                            <Badge bg="warning">{collection.totalClicks || 0}</Badge>
                          </td>
                          <td>
                            <Badge bg="danger" className="fs-6">
                              {collection.popularityScore || 0}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* En Popüler Sayfalar */}
      <Row className="mt-4">
        <Col>
          <Card>
            <Card.Header>
              <h5>
                <FontAwesomeIcon icon={faEye} className="me-2" />
                En Popüler Sayfalar
              </h5>
            </Card.Header>
            <Card.Body>
              {popularPages.length === 0 ? (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-muted mb-2" size="2x" />
                  <p className="text-muted">Bu tarih aralığında sayfa görüntüleme verisi bulunamadı.</p>
                </div>
              ) : (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Sıra</th>
                      <th>Sayfa</th>
                      <th>Görüntüleme</th>
                      <th>Ort. Süre (sn)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {popularPages.map((page, index) => (
                      <tr key={index}>
                        <td>
                          <Badge bg="primary">#{index + 1}</Badge>
                        </td>
                        <td>
                          <div>
                            <strong>{page.pageTitle || page.pagePath || 'Bilinmeyen'}</strong>
                            <br />
                            <small className="text-muted">{page.pagePath}</small>
                          </div>
                        </td>
                        <td>
                          <Badge bg="info">{page.totalViews || 0}</Badge>
                        </td>
                        <td>
                          <Badge bg="secondary">{page.avgTimeSpent || 0}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Performans Test Bölümü */}
      <Row className="mt-4">
        <Col md={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5>
                <FontAwesomeIcon icon={faChartLine} className="me-2" />
                Performans Testi
              </h5>
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={runPerformanceTest}
                disabled={performanceLoading}
              >
                {performanceLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Test Çalışıyor...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faRefresh} className="me-2" />
                    Performans Testi Yap
                  </>
                )}
              </Button>
            </Card.Header>
            <Card.Body>
              {performanceTest ? (
                <div>
                  <div className="mb-3">
                    <Badge bg="success" className="fs-6">
                      Toplam Süre: {performanceTest.performance.totalTimeMs}ms
                    </Badge>
                  </div>
                  
                  <Row>
                    <Col md={4}>
                      <Card className="border-0 bg-light">
                        <Card.Body className="text-center">
                          <h6 className="text-primary">Popüler Ürünler</h6>
                          <Badge bg="info" className="fs-6">
                            {performanceTest.results.popularProducts.count} adet
                          </Badge>
                          {performanceTest.results.popularProducts.error && (
                            <div className="text-danger mt-2 small">
                              Hata: {performanceTest.results.popularProducts.error}
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                    
                    <Col md={4}>
                      <Card className="border-0 bg-light">
                        <Card.Body className="text-center">
                          <h6 className="text-success">Popüler Koleksiyonlar</h6>
                          <Badge bg="warning" className="fs-6">
                            {performanceTest.results.popularCollections.count} adet
                          </Badge>
                          {performanceTest.results.popularCollections.error && (
                            <div className="text-danger mt-2 small">
                              Hata: {performanceTest.results.popularCollections.error}
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                    
                    <Col md={4}>
                      <Card className="border-0 bg-light">
                        <Card.Body className="text-center">
                          <h6 className="text-info">Genel İstatistikler</h6>
                          <Badge bg="secondary" className="fs-6">
                            {performanceTest.results.stats.error ? 'Hata' : 'Başarılı'}
                          </Badge>
                          {performanceTest.results.stats.error && (
                            <div className="text-danger mt-2 small">
                              Hata: {performanceTest.results.stats.error}
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  
                  <div className="mt-3">
                    <small className="text-muted">
                      <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                      Bu test, analytics sisteminin performansını ve 2000+ kayıt ile çalışma durumunu test eder.
                    </small>
                  </div>
                </div>
              ) : (
                <div className="text-center text-muted">
                  <FontAwesomeIcon icon={faChartLine} size="3x" className="mb-3 opacity-50" />
                  <p>Analytics performansını test etmek için yukarıdaki butona tıklayın.</p>
                  <small>Bu test, 2000+ kayıt ile sistemin nasıl çalıştığını gösterir.</small>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AnalyticsDashboard;
